import React from 'react';
import Button from '../../components/Button';
import AppStatusTimeline from '../../components/AppStatusTimeline';
import AutoSaveIndicator from '../../components/AutoSaveIndicator';
import InformationCollectionChat from './components/InformationCollectionChat';
import { Link } from 'react-router-dom';

// Define keyframe animation for progress bar
const progressAnimation = `
@keyframes progressGrow {
  0% { width: 0%; }
  20% { width: 10%; }
  40% { width: 15%; }
  60% { width: 20%; }
  80% { width: 25%; }
  100% { width: 30%; }
}
`;

// Add the keyframes to document head
const addProgressAnimation = () => {
  const existingStyle = document.getElementById('progress-animation-style');
  if (!existingStyle) {
    const styleEl = document.createElement('style');
    styleEl.id = 'progress-animation-style';
    styleEl.innerHTML = progressAnimation;
    document.head.appendChild(styleEl);
  }
};

// Call this function when the component first loads
addProgressAnimation();

// Helper function to format dates
const formatDate = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  // Using en-US locale for English format, adjust if needed
  return date.toLocaleString('en-US', {
    hour: 'numeric',
    minute: 'numeric',
    hour12: true, // Using 12-hour format for US standard
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });
};

// Welcome Card Component
const WelcomeCard = ({ title, description, estimatedTime, onStart, isStarting }) => (
  <div className="absolute top-0 w-[650px] left-1/2 -translate-x-1/2 transform transition-all duration-700 ease-in-out md:w-[650px] w-[95vw] max-w-[650px]">
    <div className="bg-gradient-to-br from-[#1e1e1e] to-[#121212] rounded-xl p-4 md:p-6 border-2 border-primary shadow-xl shadow-primary/20 h-[520px] relative overflow-y-auto">
      {/* Top border accent */}
      <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-primary/70 via-primary to-primary/70"></div>
    
      {/* Header with Logo */}
      <div className="flex items-center justify-between mb-6 md:mb-8 pb-3 md:pb-5 border-b border-gray-800/50">
        <h2 className="text-xl md:text-2xl font-bold text-white">{title}</h2>
        <img src="/images/logo-new.png" alt="ELEF89 Logo" className="h-8 md:h-10 w-auto" />
      </div>
      
      {/* Main Content */}
      <div className="space-y-6 md:space-y-8">
        {/* Description Section */}
        <div className="flex flex-col md:flex-row gap-3 md:gap-5">
          <div className="w-10 h-10 md:w-12 md:h-12 rounded-xl bg-primary/20 flex items-center justify-center flex-shrink-0 border border-primary/50">
            <svg className="w-5 h-5 md:w-6 md:h-6 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
            </svg>
          </div>
          <div>
            <h3 className="text-base md:text-lg font-semibold text-white mb-2 md:mb-3">Getting Started</h3>
            <p className="text-gray-300 text-sm md:text-md leading-relaxed">
              {description}
            </p>
          </div>
        </div>
        
        {/* What to Expect Section */}
        <div className="flex flex-col md:flex-row gap-3 md:gap-5">
          <div className="w-10 h-10 md:w-12 md:h-12 rounded-xl bg-primary/20 flex items-center justify-center flex-shrink-0 border border-primary/50">
            <svg className="w-5 h-5 md:w-6 md:h-6 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <div>
            <h3 className="text-base md:text-lg font-semibold text-white mb-2 md:mb-3">What to Expect</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 md:gap-3">
              <div className="flex items-start gap-2">
                <svg className="w-4 h-4 md:w-5 md:h-5 text-primary mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <span className="text-gray-300 text-xs md:text-sm">Step-by-step guidance through the entire process</span>
              </div>
              <div className="flex items-start gap-2">
                <svg className="w-4 h-4 md:w-5 md:h-5 text-primary mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <span className="text-gray-300 text-xs md:text-sm">Automated setup for Google Play Store requirements</span>
              </div>
              <div className="flex items-start gap-2">
                <svg className="w-4 h-4 md:w-5 md:h-5 text-primary mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <span className="text-gray-300 text-xs md:text-sm">Email notifications when each stage is ready</span>
              </div>
              <div className="flex items-start gap-2">
                <svg className="w-4 h-4 md:w-5 md:h-5 text-primary mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <span className="text-gray-300 text-xs md:text-sm">Support available throughout the deployment process</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Footer with Button & Time Estimate */}
      <div className="absolute bottom-0 left-0 right-0 px-4 md:px-6 py-3 md:py-5 border-t border-gray-800/50 flex items-center justify-between">
        <p className="text-xs md:text-sm text-gray-500 flex items-center gap-2">
          <svg className="w-4 h-4 md:w-5 md:h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          Estimated Time: {estimatedTime}
        </p>
      </div>
    </div>
  </div>
);

// Information Card Component
const InformationCard = ({ 
  stage, 
  index,
  currentIndex,
  timestamp
}) => (
  <div
    className={`absolute top-0 w-[95vw] md:w-[650px] max-w-[650px] transition-all duration-700 ease-in-out transform ${
      index === currentIndex 
        ? 'opacity-100 left-1/2 -translate-x-1/2 scale-100 z-20 rotate-0'
        : index === currentIndex + 1
        ? 'opacity-60 left-[120%] -translate-x-1/2 scale-90 z-10 rotate-6 brightness-75'
        : index === currentIndex - 1
        ? 'opacity-0 left-[-20%] -translate-x-1/2 scale-90 z-10 -rotate-6 brightness-75'
        : index < currentIndex
        ? 'opacity-0 -left-[80%] -translate-x-1/2 scale-80 z-0 -rotate-12'
        : 'opacity-0 left-[180%] -translate-x-1/2 scale-80 z-0 rotate-12'
    }`}
  >
    <div className="bg-gradient-to-br from-[#1e1e1e] to-[#121212] rounded-xl border-2 border-blue-500 shadow-lg shadow-blue-500/20 h-[520px] overflow-y-auto relative">
      {/* Top border accent */}
      <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-blue-500/70 via-blue-400 to-blue-500/70"></div>
    
      {/* Header with info icon */}
      <div className="flex items-center justify-between px-4 md:px-6 pt-4 md:pt-5 pb-3 md:pb-4">
        <div className="flex items-center gap-3 md:gap-4">
          <div className="w-10 h-10 md:w-12 md:h-12 rounded-xl bg-blue-900/30 text-blue-400 border border-blue-700/50 flex items-center justify-center flex-shrink-0">
            <svg className="w-6 h-6 md:w-7 md:h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <div>
            <div className="flex items-center flex-wrap gap-2 md:gap-3">
              <h2 className="text-lg md:text-xl font-bold text-white">{stage.title}</h2>
              <span className="text-[10px] md:text-xs font-medium px-2 py-1 rounded-full bg-blue-900/30 text-blue-400 border border-blue-700/50">
                Information
              </span>
            </div>
            {timestamp && (
              <p className="text-xs md:text-sm text-gray-400 mt-1">
                Viewed: {formatDate(timestamp)}
              </p>
            )}
          </div>
        </div>
        
        <img src="/images/logo-new.png" alt="ELEF89 Logo" className="h-6 md:h-8 w-auto" />
      </div>
      
      {/* Main content area */}
      <div className="px-4 md:px-6 pb-20">
        {/* Description with enhanced visual divider */}
        <div className="mb-4 md:mb-6">
          <div className="flex items-center gap-2 mb-2 md:mb-3">
            <div className="w-1 h-5 bg-gradient-to-b from-blue-500 via-blue-500 to-blue-500/50 rounded-full"></div>
            <h3 className="text-sm md:text-base font-semibold text-white">Information</h3>
          </div>
          <p className="text-xs md:text-sm leading-relaxed text-gray-300">
            {stage.description}
          </p>
        </div>
        
        {/* Additional Information (if exists) */}
        {stage.additionalInfo && (
          <div className="mb-4 md:mb-6">
            <div className="flex items-center gap-2 mb-2 md:mb-3">
              <div className="w-1 h-5 bg-gradient-to-b from-blue-500 via-blue-500 to-blue-500/50 rounded-full"></div>
              <h3 className="text-sm md:text-base font-semibold text-white">Additional Details</h3>
            </div>
            <p className="text-xs md:text-sm leading-relaxed text-gray-300">
              {stage.additionalInfo}
            </p>
          </div>
        )}
      </div>
      
      {/* Footer */}
      <div className="absolute bottom-0 left-0 right-0">
        <div className="px-4 md:px-6 py-3 md:py-4 border-t border-gray-800 flex items-center justify-between">
          <p className="text-[10px] md:text-xs text-gray-400 flex items-center gap-2">
            <svg className="w-3 h-3 md:w-4 md:h-4 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            Information card - no action required
          </p>
        </div>
      </div>
    </div>
  </div>
);

// Stage Card Component
const StageCard = ({ 
  stage, 
  index,
  currentIndex,
  isActive,
  isCompleted,
  onNextStage, 
  onPrevStage,
  onStartSubsteps,
  startSubstepsLoading,
  children,
  formData,
  timestamp,
  storeUrl
}) => (
  <div
    className={`absolute top-0 w-[95vw] md:w-[650px] max-w-[650px] transition-all duration-700 ease-in-out transform ${
      index === currentIndex 
        ? 'opacity-100 left-1/2 -translate-x-1/2 scale-100 z-20 rotate-0'
        : index === currentIndex + 1
        ? 'opacity-60 left-[120%] -translate-x-1/2 scale-90 z-10 rotate-6 brightness-75'
        : index === currentIndex - 1
        ? 'opacity-0 left-[-20%] -translate-x-1/2 scale-90 z-10 -rotate-6 brightness-75'
        : index < currentIndex
        ? 'opacity-0 -left-[80%] -translate-x-1/2 scale-80 z-0 -rotate-12'
        : 'opacity-0 left-[180%] -translate-x-1/2 scale-80 z-0 rotate-12'
    }`}
  >
    {/* Clean, minimalist card with all content visible and enhanced borders */}
    <div className={`
      bg-gradient-to-br from-[#1e1e1e] to-[#121212] 
      rounded-xl
      border-2
      ${isActive 
        ? 'border-primary shadow-lg shadow-primary/20' 
        : isCompleted 
        ? 'border-green-600 shadow-lg shadow-green-900/10' 
        : 'border-gray-700'
      }
      h-[520px] overflow-y-auto relative
      ${index === currentIndex ? 'bg-clip-padding backdrop-filter backdrop-blur-sm' : ''}
    `}>
      {/* Top border accent */}
      <div className={`absolute top-0 left-0 right-0 h-1 ${
        isActive 
          ? 'bg-gradient-to-r from-primary/70 via-primary to-primary/70' 
          : isCompleted 
          ? 'bg-gradient-to-r from-green-600/70 via-green-500 to-green-600/70' 
          : 'bg-gradient-to-r from-gray-700/50 via-gray-600 to-gray-700/50'
      }`}></div>
    
      {/* Header with stage info and status */}
      <div className="flex items-center justify-between px-4 md:px-6 pt-4 md:pt-5 pb-3 md:pb-4">
        <div className="flex items-center gap-3 md:gap-4">
          <div className={`w-10 h-10 md:w-12 md:h-12 rounded-xl flex items-center justify-center flex-shrink-0 border
            ${isCompleted 
              ? 'bg-green-900/30 text-green-400 border-green-700/50' 
              : isActive 
              ? 'bg-primary/20 text-primary border-primary/50' 
              : 'bg-gray-800/50 text-gray-500 border-gray-700/50'
            }`}>
            {isCompleted ? (
              <svg className="w-6 h-6 md:w-7 md:h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            ) : isActive ? (
              <svg className="w-6 h-6 md:w-7 md:h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
            ) : (
              <svg className="w-6 h-6 md:w-7 md:h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            )}
          </div>
          <div>
            <div className="flex flex-wrap items-center gap-2 md:gap-3">
              <h2 className="text-lg md:text-xl font-bold text-white">{stage.title}</h2>
              <span className={`text-[10px] md:text-xs font-medium px-2 py-1 rounded-full border ${
                isCompleted 
                  ? 'bg-green-900/30 text-green-400 border-green-700/50' 
                  : isActive 
                  ? 'bg-primary/20 text-primary border-primary/50' 
                  : 'bg-gray-800 text-gray-500 border-gray-700/50'
              }`}>
                {isCompleted ? 'Completed' : isActive ? 'In Progress' : 'Pending'}
              </span>
            </div>
            {timestamp && (
              <p className="text-xs md:text-sm text-gray-400 mt-1">
                Started: {formatDate(timestamp)}
              </p>
            )}
          </div>
        </div>
        
        <img src="/images/logo-new.png" alt="ELEF89 Logo" className="h-6 md:h-8 w-auto" />
      </div>
      
      {/* Main content area */}
      <div className="px-4 md:px-6 pb-20">
        {children || (
          <>
            {/* Completion message when stage is completed */}
            {isCompleted && (
              <div className="mb-4 md:mb-6 bg-green-900/30 p-3 md:p-4 rounded-xl border border-green-700/50 text-center">
                <div className="flex flex-col items-center gap-2 md:gap-3 mb-2 md:mb-3">
                  <div className="w-12 h-12 md:w-16 md:h-16 rounded-full bg-green-900/50 flex items-center justify-center flex-shrink-0">
                    <svg className="w-6 h-6 md:w-8 md:h-8 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-lg md:text-xl font-semibold text-green-400 mb-1 md:mb-2">Stage Completed</h3>
                    <p className="text-sm md:text-md text-gray-300">
                      You've successfully completed this stage. Your app is now moving to the next phase of deployment.
                    </p>
                  </div>
                </div>
                {storeUrl && (
                  <a 
                    href={storeUrl} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="flex items-center justify-center gap-2 text-blue-400 hover:text-blue-300 mt-3 md:mt-4 transition-colors text-sm md:text-base"
                  >
                    <svg className="w-4 h-4 md:w-5 md:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                    </svg>
                    View your app in Google Play Store
                  </a>
                )}
                <p className="text-xs md:text-sm text-gray-400 mt-3 md:mt-4">
                  You can now click the "Continue" button below to proceed to the next stage.
                </p>
              </div>
            )}
            
            {/* Description with enhanced visual divider - Only shown if stage is not completed */}
            {!isCompleted && (
              <div className="mb-4 md:mb-6">
                <div className="flex items-center gap-2 mb-2 md:mb-3">
                  <div className="w-1 h-5 bg-gradient-to-b from-primary via-primary to-primary/50 rounded-full"></div>
                  <h3 className="text-sm md:text-base font-semibold text-white">Overview</h3>
                </div>
                <p className="text-xs md:text-sm text-gray-300 leading-relaxed">
                  {stage.description}
                </p>
              </div>
            )}
            
            {/* Additional Information (if exists) - Show even if completed */}
            {stage.additionalInfo && !isCompleted && (
              <div className="mb-4 md:mb-6">
                <div className="flex items-center gap-2 mb-2 md:mb-3">
                  <div className="w-1 h-5 bg-gradient-to-b from-primary via-primary to-primary/50 rounded-full"></div>
                  <h3 className="text-sm md:text-base font-semibold text-white">Additional Information</h3>
                </div>
                <p className="text-xs md:text-sm text-gray-300 leading-relaxed">
                  {stage.additionalInfo}
                </p>
              </div>
            )}
            
            {/* Help section for closedTesting - Only shown if not completed */}
            {stage.id === 'closedTesting' && !isCompleted && (
              <div className="mb-4 md:mb-6">
                <div className="flex items-center gap-2 mb-2 md:mb-3">
                  <div className="w-1 h-5 bg-gradient-to-b from-primary via-primary to-primary/50 rounded-full"></div>
                  <h3 className="text-sm md:text-base font-semibold text-white">Why We Need This Information</h3>
                </div>
                <div className="text-xs md:text-sm text-gray-300 leading-relaxed">
                  <p className="mb-2">To set up your app for closed testing, we need to gather some essential information. This helps us:</p>
                  <ul className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    <li className="flex items-start gap-2">
                      <svg className="w-3 h-3 md:w-4 md:h-4 text-primary mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      <span>Ensure your app meets Google Play Store requirements</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <svg className="w-3 h-3 md:w-4 md:h-4 text-primary mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      <span>Configure the appropriate testing environment</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <svg className="w-3 h-3 md:w-4 md:h-4 text-primary mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      <span>Set up content ratings and safety measures</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <svg className="w-3 h-3 md:w-4 md:h-4 text-primary mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      <span>Prepare your app for a smooth testing phase</span>
                    </li>
                  </ul>
                </div>
              </div>
            )}
            
            {/* Action Button for closedTesting - Only shown if not started and not completed */}
            {stage.id === 'closedTesting' && !formData?.[`${stage.id}_started`] && !isCompleted && (
              <div className="absolute bottom-20 left-4 right-4 md:left-6 md:right-6">
                <Button
                  onClick={() => onStartSubsteps(stage.id)}
                  disabled={startSubstepsLoading}
                  className="w-full py-2 md:py-3 bg-gradient-to-r from-primary to-primary-light hover:from-primary-light hover:to-primary text-white rounded-xl shadow-lg transform hover:scale-105 transition-all duration-300 flex items-center justify-center gap-2 md:gap-3 border border-primary/50 text-sm md:text-base"
                >
                  {startSubstepsLoading ? (
                    <><svg className="animate-spin h-4 w-4 md:h-5 md:w-5" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" /></svg><span>Loading...</span></>
                  ) : (
                    <><svg className="w-4 h-4 md:w-5 md:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" /></svg><span>Start Questions Process</span></>
                  )}
                </Button>
              </div>
            )}
          </>
        )}
      </div>
      
      {/* Footer with notification info */}
      <div className="absolute bottom-0 left-0 right-0">
        <div className="px-4 md:px-6 py-3 md:py-4 border-t border-gray-800 flex items-center justify-between">
          <div className="flex flex-col">
            {isCompleted ? (
              <p className="text-[10px] md:text-xs text-green-400 flex items-center gap-1 md:gap-2">
                <svg className="w-3 h-3 md:w-4 md:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                Step completed successfully
              </p>
            ) : (
              <p className="text-[10px] md:text-xs text-gray-400 flex items-center gap-1 md:gap-2">
                <svg className="w-3 h-3 md:w-4 md:h-4 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                You'll receive an email notification when ready
              </p>
            )}
            {storeUrl && (
              <a 
                href={storeUrl} 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-[10px] md:text-xs text-blue-400 hover:text-blue-300 flex items-center gap-1 md:gap-2 mt-1"
              >
                <svg className="w-3 h-3 md:w-4 md:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                </svg>
                View in Store
              </a>
            )}
          </div>
          
          {/* Progress indicator */}
          <div className="w-20 md:w-32">
            <div className="h-1 bg-gray-800 rounded-full overflow-hidden">
              {isActive ? (
                <div 
                  className="h-full bg-primary" 
                  style={{
                    width: '0%',
                    animation: 'progressGrow 20s ease-out forwards'
                  }}
                ></div>
              ) : isCompleted ? (
                <div className="h-full bg-green-500" style={{width: '100%'}}></div>
              ) : (
                <div className="h-full bg-gray-800" style={{width: '0%'}}></div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
);

// Main View Component
const AppStatusView = ({
  fileId,
  showWelcomeCard,
  error,
  cardIndex,
  cardTemplates,
  deployStatus,
  isLoading,
  formData,
  isSaving,
  lastSavedAt,
  onStartDeployment,
  onNextCard,
  onPrevCard,
  onStartSubsteps,
  onInputChange,
  onSubmitAnswer,
  isStageCompleted,
  isSubstepCompleted,
  isCurrentStageInProgress,
  getNextIncompleteStageIndex,
  isNextIncompleteStage,
  startSubstepsLoading
}) => {
  // Log props on render for debugging
  console.log('AppStatusView rendering with props:', { fileId, showWelcomeCard, cardIndex, isLoading, error, cardTemplatesLength: cardTemplates?.length });
  
  // Loading state display
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-bg-black">
        <div className="text-center">
          <div className="w-12 h-12 md:w-16 md:h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-lg md:text-xl text-white mb-2">Loading Process Details...</p>
          <p className="text-xs md:text-sm text-gray-400">Connecting to the server and preparing data</p>
        </div>
      </div>
    );
  }

  // Error state display if no fileId
  if (!fileId) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-bg-black p-4">
        <div className="text-center bg-red-900/30 p-4 md:p-6 rounded-xl max-w-md">
          <div className="w-10 h-10 md:w-12 md:h-12 mx-auto mb-4 text-red-500">
            <svg className="w-full h-full" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h2 className="text-lg md:text-xl font-bold text-white mb-2">Error Loading Process</h2>
          <p className="text-sm md:text-base text-gray-300 mb-4">
            Process identifier (fileId) not found. Check the URL and try again.
          </p>
          <a href="/dashboard" className="inline-block px-3 py-2 md:px-4 md:py-2 bg-primary text-white rounded-lg hover:bg-primary-light transition-colors text-sm md:text-base">
            Back to Dashboard
          </a>
        </div>
      </div>
    );
  }

  // Main component structure
  return (
    <div className="min-h-screen bg-bg-black text-text-white">
      <div className="max-w-7xl mx-auto px-4 py-6 md:py-12">
        {/* Display general error messages */}
        {error && (
          <div className="text-red-500 text-center mb-6 md:mb-8 text-base md:text-lg">
            Error: {error}
          </div>
        )}

        <div className="flex flex-col md:flex-row">
          {/* Timeline - Hidden on mobile, visible on desktop */}
          {cardTemplates?.length > 0 && (
            <div className="hidden md:block">
              <AppStatusTimeline 
                stages={cardTemplates.filter(card => card.type === 'stage')}
                infoCards={cardTemplates.filter(card => card.type === 'information')}
                currentStage={deployStatus?.currentStage}
                deployStatus={deployStatus} 
              />
            </div>
          )}

          {/* Card Container - Centered on both mobile and desktop */}
          <div className="flex-1 flex justify-center">
            {/* The relative container where cards are positioned */}
            <div className="relative w-full md:w-[740px] h-[520px] md:h-[620px]">
              {/* Welcome Card */}
              {showWelcomeCard && cardTemplates?.length > 0 && cardTemplates[0] && (
                <>
                  <WelcomeCard 
                    title={cardTemplates[0].title || "Start the Deployment Process"}
                    description={cardTemplates[0].description || "Welcome to the app deployment process. We'll guide you through all the steps."}
                    estimatedTime={cardTemplates[0].estimatedTime || "20-30 minutes"}
                    onStart={() => { console.log('WelcomeCard onStart clicked'); onStartDeployment(); }}
                    isStarting={isLoading}
                  />
                  <InformationCard
                    stage={cardTemplates[0]}
                    index={0}
                    currentIndex={0}
                    timestamp={deployStatus?.informationViewed?.[cardTemplates[0].id]?.viewedAt}
                  />
                </>
              )}

              {/* Information Cards */}
              {!showWelcomeCard && cardTemplates?.filter(card => card.type === 'information').map((infoCard, infoIndex) => {
                // Find the position of this card in the combined sequence of information and stage cards
                const allVisibleCards = cardTemplates.filter(card => card.type === 'stage' || card.type === 'information');
                const visibleCardIndex = allVisibleCards.findIndex(card => card.id === infoCard.id);
                const currentVisibleCardIndex = cardIndex - 1;
                
                return (
                  <InformationCard
                    key={infoCard.id}
                    stage={infoCard}
                    index={visibleCardIndex}
                    currentIndex={currentVisibleCardIndex}
                    timestamp={deployStatus?.informationViewed?.[infoCard.id]?.viewedAt}
                  />
                );
              })}

              {/* Stage Cards */}
              {!showWelcomeCard && cardTemplates?.filter(card => card.type === 'stage').map((stage, stageIndex) => {
                // Calculate the current index relative to stages (cardIndex 0 is welcome)
                // cardIndex comes from AppStatusLogic state
                const currentStageViewIndex = cardIndex - 1; 
                console.log(`Rendering StageCard for: ${stage.id}, Stage Index: ${stageIndex}, Current Active Stage Index: ${currentStageViewIndex}`); // Updated Log
                
                // Check if stage is completed
                const completed = isStageCompleted(stage.id);
                // A stage is active if it's the current one AND not completed
                const active = stageIndex === currentStageViewIndex && !completed;
                
                // Pass stageIndex as 'index' and currentStageViewIndex as 'currentIndex' to StageCard
                return (
                  <StageCard
                    key={stage.id}
                    stage={stage}
                    index={stageIndex} // This card's index in the filtered array
                    currentIndex={currentStageViewIndex} // The index of the active stage
                    isActive={active}
                    isCompleted={completed}
                    onNextStage={onNextCard}
                    onPrevStage={onPrevCard}
                    onStartSubsteps={onStartSubsteps}
                    formData={formData}
                    timestamp={deployStatus?.stages?.[stage.id]?.startedAt}
                    startSubstepsLoading={startSubstepsLoading}
                    storeUrl={deployStatus?.storeUrl}
                  >
                    {/* Render substeps component (e.g., InformationCollectionChat) */}
                    {stage.id === 'closedTesting' && 
                     formData?.[`${stage.id}_started`] && 
                     stage.substeps && (
                      <InformationCollectionChat 
                        substeps={stage.substeps}
                        stageId={stage.id}
                        onInputChange={onInputChange}
                        onSubmitAnswer={onSubmitAnswer}
                        formData={formData}
                        isSubstepCompleted={(substepId) => isSubstepCompleted(stage.id, substepId)}
                        onNextStage={onNextCard}
                      />
                    )}
                  </StageCard>
                );
              })}
            </div>
          </div>
        </div>

        {/* Current stage indicator for mobile - Shown only on mobile */}
        {!showWelcomeCard && cardTemplates?.length > 0 && (
          <div className="mt-4 mb-6 md:hidden flex justify-center">
            <div className="flex gap-1.5">
              {cardTemplates.filter(card => card.type === 'stage').map((_, idx) => (
                <div 
                  key={idx} 
                  className={`w-2 h-2 rounded-full ${
                    idx === cardIndex - 1 
                      ? 'bg-primary' 
                      : idx < cardIndex - 1 
                      ? 'bg-green-500' 
                      : 'bg-gray-600'
                  }`}
                ></div>
              ))}
            </div>
          </div>
        )}

        {/* Navigation Buttons - Modified to always show Next button */}
        <div className="fixed bottom-4 md:bottom-8 left-1/2 -translate-x-1/2 flex gap-3 md:gap-4 z-50">
          {/* Only show Previous button after welcome card */}
          {!showWelcomeCard && (
            <button
              onClick={() => { 
                console.log('Prev Button clicked. Current local cardIndex:', cardIndex); 
                // Call the previous card function directly
                onPrevCard(); 
              }}
              // Previous button should be enabled when not on the first card
              // Note: We allow navigation to any completed card
              disabled={cardIndex <= 1}
              className={`px-4 md:px-6 py-2 md:py-3 rounded-xl text-sm md:text-base transition-all duration-300 ${
                cardIndex <= 1
                  ? 'bg-gray-800 text-gray-600 cursor-not-allowed'
                  : 'bg-primary text-white hover:bg-primary-light'
              }`}
            >
              Previous
            </button>
          )}
          
          {/* Next button - hide on welcome card and on the last card */}
          {(showWelcomeCard || (cardTemplates && cardIndex < cardTemplates.length - 1)) && (
            <button
              onClick={() => { 
                console.log('Next Button clicked. Current local cardIndex:', cardIndex); 
                if (showWelcomeCard) {
                  onStartDeployment(); // Start deployment process on welcome card
                } else {
                  onNextCard(); // Normal next behavior for other cards
                }
              }}
              // Enable the button when current card is completed or it's an information card
              // On welcome card, button is always enabled
              disabled={
                !showWelcomeCard && (
                  !cardTemplates || 
                  cardTemplates.length === 0 || 
                  // Only block navigation if the current stage is not completed
                  (deployStatus?.stages && cardTemplates && cardIndex >= 1 && 
                   cardTemplates[cardIndex]?.type === 'stage' && 
                   !isStageCompleted(cardTemplates[cardIndex].id) && 
                   cardTemplates[cardIndex]?.type !== 'information')
                )
              }
              className={`px-4 md:px-6 py-2 md:py-3 rounded-xl text-sm md:text-base transition-all duration-300 flex items-center gap-1 md:gap-2 ${
                !showWelcomeCard && (
                  !cardTemplates || 
                  cardTemplates.length === 0 || 
                  (deployStatus?.stages && cardTemplates && cardIndex >= 1 && 
                   cardTemplates[cardIndex]?.type === 'stage' && 
                   !isStageCompleted(cardTemplates[cardIndex].id) &&
                   cardTemplates[cardIndex]?.type !== 'information')
                )
                  ? 'bg-gray-800 text-gray-600 cursor-not-allowed'
                  : 'bg-primary text-white hover:bg-primary-light'
              }`}
            >
              {/* Add an animated indicator when stage is completed to prompt user to click Next */}
              {cardTemplates && cardIndex >= 1 && 
               cardTemplates[cardIndex]?.type === 'stage' && 
               isStageCompleted(cardTemplates[cardIndex].id) && (
                <svg className="w-4 h-4 md:w-5 md:h-5 animate-pulse" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                </svg>
              )}
              {showWelcomeCard 
                ? "Start Process" 
                : (cardTemplates && cardIndex >= 1 && 
                   cardTemplates[cardIndex]?.type === 'stage' && 
                   isStageCompleted(cardTemplates[cardIndex].id))
                  ? "Continue" 
                  : "Next"
              }
            </button>
          )}
        </div>
      </div>

      {/* Auto Save Indicator */}
      <AutoSaveIndicator isSaving={isSaving} lastSavedAt={lastSavedAt} />
    </div>
  );
};

export default AppStatusView; 